import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Badge,
  PageHeader,
  Skeleton,
  formatDateTimeVn,
  formatVnd,
} from "@chotca/ui";
import { usePlanPurchaseHistory } from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";
import type { OrgPlanPurchase } from "@chotca/config";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";

type Purchase = OrgPlanPurchase;

const STATUS_TONE: Record<
  Purchase["status"],
  "lime" | "sun" | "smoke" | "ember"
> = {
  PAID: "lime",
  PENDING: "sun",
  EXPIRED: "smoke",
  FAILED: "ember",
};

// Employer billing history — every plan/top-up order for the org, any status.
// Card list (not a table) so the status badge + amount never overflow on a
// narrow mobile screen.
export function BillingClient() {
  const t = useTranslations("billing");
  const tDash = useTranslations("dashboard");
  const orgId = useAuthStore((s) => s.activeOrgId);
  const { data, isLoading } = usePlanPurchaseHistory(orgId);
  const { openUpgrade } = useSubscriptionModals();

  const items = (data?.items ?? []) as Purchase[];

  const itemLabel = (p: Purchase): string => {
    if (p.kind === "PLAN") return t("itemPlan", { plan: p.planCode ?? "" });
    if (p.kind === "SOURCING")
      return t("itemSourcing", { qty: p.quantity ?? 0 });
    return t("itemBoost", { qty: p.quantity ?? 0 });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
        actions={
          <button
            type="button"
            onClick={() => openUpgrade()}
            className="rounded-full bg-lime px-5 py-2.5 text-sm font-bold text-ink transition hover:brightness-95"
          >
            {tDash("planCardManageCta")}
          </button>
        }
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-2xl" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="rounded-2xl border border-smoke bg-paper p-6 text-center text-sm text-ash">
          {t("empty")}
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((p) => (
            <li key={p.id}>
              <Link
                to={`/billing/${encodeURIComponent(p.orderCode)}`}
                className="flex items-center gap-3 rounded-2xl border border-smoke bg-paper p-4 transition hover:border-ash hover:bg-mist"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">
                    {itemLabel(p)}
                  </p>
                  <p className="mt-0.5 text-xs text-ash">
                    {formatDateTimeVn(p.createdAt)}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <span className="font-mono text-sm font-bold text-ink">
                    {formatVnd(p.amountVnd)}
                  </span>
                  <Badge tone={STATUS_TONE[p.status]}>
                    {t(`status${p.status}`)}
                  </Badge>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
